import { Link } from "react-router-dom";
import type { PlayerReport } from "../../../api/playerReports";
import { ReportChart } from "./ReportChart";
import { QuestionnaireScores } from "./ReportReading";

const date = (s: string | null) =>
  s ? new Date(s).toLocaleDateString("es-AR") : "Sin toma registrada";
const num = (n: number) =>
  n.toLocaleString("es-AR", { maximumFractionDigits: 2 });
export function PlayerProfileSection({ data }: { data: PlayerReport }) {
  const charts = data.charts.filter(
    (c) =>
      c.id.startsWith("questionnaire-") &&
      c.id !== "questionnaire-participation",
  );
  return (
    <section
      id="perfil"
      className="report-section"
      aria-labelledby="profile-heading"
    >
      <div className="report-section-heading">
        <h2 id="profile-heading">Qué recursos reconocés en vos</h2>
        <Link to="/data#cuestionarios">Mis cuestionarios →</Link>
      </div>
      <p className="report-caption">{data.evidence.profile_note}</p>
      <div className="report-instruments">
        {data.evidence.profile.map((group) => (
          <article className="report-instrument" key={group.code}>
            <header>
              <div>
                <h3>{group.title}</h3>
                <p className="report-caption">
                  {group.code} · {date(group.date)}
                  {group.timepoint ? ` · ${group.timepoint}` : ""}
                </p>
              </div>
            </header>
            {group.dimensions.every((d) => !d.score) ? (
              <p className="report-empty-inline">
                {group.availability}. Completá {group.code} para sumar tu
                mirada.
              </p>
            ) : (
              <>
                <dl className="report-dimension-rows">
                  {group.dimensions.map((d) => (
                    <div key={d.key}>
                      <dt>{d.label}</dt>
                      <dd>
                        {d.score ? (
                          <>
                            <strong>
                              {num(d.score.mean)}
                              <small> / 4</small>
                            </strong>
                            <span
                              className="report-dimension-track"
                              role="img"
                              aria-label={`${d.label}: ${num(d.score.mean)} de 4. Escala descriptiva de 1 a 4.`}
                            >
                              <i
                                style={{
                                  left: `${((d.score.mean - 1) / 3) * 100}%`,
                                }}
                              />
                            </span>
                          </>
                        ) : (
                          "Sin puntaje"
                        )}
                      </dd>
                      {d.change !== null && (
                        <p className="report-caption">
                          {d.change === 0
                            ? "Sin cambio"
                            : `${d.change > 0 ? "+" : "−"}${num(Math.abs(d.change))}`}{" "}
                          desde {date(d.previous_date)} · descriptivo
                        </p>
                      )}
                    </div>
                  ))}
                </dl>
                {group.total && (
                  <p className="report-caption report-instrument-total">
                    SMTQ total: <strong>{group.total.raw}</strong> · rango{" "}
                    {group.total.min}–{group.total.max}
                  </p>
                )}
              </>
            )}
            <details className="report-row-context">
              <summary>Qué significa y cómo se corrige</summary>
              <p className="report-caption">{group.note}</p>
              {group.dimensions.map((d) => (
                <p className="report-caption" key={d.key}>
                  <strong>{d.label}.</strong> {d.meaning}
                  {d.score
                    ? ` ${d.score.raw} puntos; rango ${d.score.min}–${d.score.max}.`
                    : ""}
                </p>
              ))}
              <p className="report-caption">
                Escala 1–4, sin cortes de color. Las respuestas describen tu
                percepción.
              </p>
            </details>
          </article>
        ))}
      </div>
      {data.scope !== "trial" && (
        <>
          {charts.length > 0 && (
            <div className="report-chart-grid">
              {charts.map((c) => (
                <ReportChart key={c.id} chart={c} />
              ))}
            </div>
          )}
          <details className="report-disclosure">
            <summary>Historial y corrección de todas las tomas</summary>
            <p className="report-caption">{data.questionnaire_note}</p>
            <QuestionnaireScores questionnaires={data.questionnaires} />
          </details>
        </>
      )}
    </section>
  );
}
